import { type ReactElement, useState } from 'react';
import { Form, Link, redirect, useActionData, useLoaderData, useNavigation } from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Button, FieldError, invalidProps } from '@marketplace/ui';
import {
  QUOTE_MIN_AMOUNT_CENTS,
  type Bid,
  type JobFeedItem,
  type JobThreadView,
  type PayoutStatus,
  type ProviderSubscription,
} from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import {
  bidFailureKey,
  lowestOf,
  parseBidForm,
  type BidFormField,
} from '../features/auctions/bid-form.js';
import { AuctionHeader } from '../features/auctions/AuctionHeader.js';
import { Countdown } from '../features/auctions/Countdown.js';
import {
  isSubscriptionRefusal,
  loadMySubscription,
  subscriptionRefusalKey,
} from '../features/billing/billing.js';
import {
  SubscriptionCtaLine,
  SubscriptionRefused,
  SubscriptionRequiredBanner,
  isSubscriptionPending,
} from '../features/billing/SubscriptionRequiredBanner.js';
import { timeLeft } from '../features/auctions/time-left.js';
import { loadJobFeed } from '../features/jobs/feed-query.js';
import { FeedBudget, FeedCoverage, FeedWhere } from '../features/jobs/FeedJobFacts.js';
import { eurosToCents } from '../features/jobs/posting.js';
import { Thread } from '../features/messages/Thread.js';
import { sendMessage, type MessageActionData } from '../features/messages/message-form.js';
import { usePoll } from '../features/messages/poll.js';
import { loadMyThread } from '../features/messages/thread-query.js';
import { PayoutsCtaLine, PayoutsPendingBanner } from '../features/payouts/PayoutsPendingBanner.js';
import { loadMyPayouts, payoutsRefusalKey } from '../features/payouts/onboarding.js';
import {
  DEFAULT_VALIDITY_DAYS,
  dateInputValue,
  parseQuoteForm,
  quoteFailureKey,
  type QuoteFormField,
} from '../features/quotes/quote-form.js';
import { QuotePrice } from '../features/quotes/QuotePrice.js';
import { ApiError } from '../shared/api-error.js';
import { queryKeys, routeContext } from '../shared/query.js';
import { loadSession } from '../shared/session.js';

/**
 * One job from the feed, and the quote form on it — `W4-T10` §2.5.
 *
 * **It reads the feed, because there is no other door.** `GET /api/jobs/:id` is the owner's
 * (`job:read-own`) and no provider-facing job endpoint exists — `W4-T07` §6 named that and left it.
 * So this loads the same bounded set `/feed` does, through the same query key, and picks the row. A
 * job that is not in it is a 404: out of radius, out of trade, closed and never-existed are one
 * answer to *can I quote this*.
 */
interface FeedJobData {
  locale: string;
  item: JobFeedItem;
  /** The date input's starting value, computed in the loader so render stays pure. */
  defaultValidUntil: string;
  /** `W4-T06` — the conversation with the client. `null` when it could not be loaded. */
  thread: JobThreadView | null;
  /** `W6-T06` — this provider's own bids on the job, newest first. Never anyone else's. */
  myBids: Bid[];
  /**
   * `W6-T08`: the bids could not be loaded. Then `myBids` is empty and means nothing — the panel says
   * so and offers no form, rather than telling the provider they have not bid (spec §10.0 Q2).
   */
  myBidsError: boolean;
  /** The loader's clock — what the countdown and "is bidding still open?" are measured against. */
  now: string;
  /**
   * `W5-T01` §4.3 — whether Stripe has cleared the provider to quote and bid. `null` when it could not
   * be read: no banner, the send buttons stay enabled, and the API's gate is the judge.
   */
  payouts: PayoutStatus | null;
  /**
   * `W5-T13` §4.6 — whether the provider may quote and bid at all, the step before Stripe's. `null`
   * when it could not be read: nothing of the subscription shows, and the API's gate is the judge.
   */
  subscription: ProviderSubscription | null;
}

type QuoteActionData =
  | { errors: Partial<Record<QuoteFormField, string>> }
  | { failure: string }
  /** `W5-T08` §4.5: the job filled while the provider wrote — a notice, never a failure. */
  | { full: true }
  /** `W5-T12` §7: every window was refunded empty — a notice, never a failure. */
  | { closed: true };
type BidActionData = { bidErrors: Partial<Record<BidFormField, string>> } | { bidFailure: string };

/**
 * The provider's own bids on one job, fresh — a lowered bid or a close must show on the next poll.
 * The server picks them (`W6-T08`): no filter here, so no cap elsewhere can hide one.
 */
async function loadMyBids(context: LoaderFunctionArgs['context'], jobId: string): Promise<Bid[]> {
  const { queryClient, api } = context.get(routeContext);
  return queryClient.fetchQuery({
    queryKey: queryKeys.myBids(jobId),
    queryFn: () => api.getMyBidsForJob(jobId),
    staleTime: 0,
  });
}

export async function loader({ params, context }: LoaderFunctionArgs): Promise<FeedJobData> {
  const locale = localeOf(params);

  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  const id = params['id'] ?? '';
  const item = (await loadJobFeed(context)).find((job) => job.id === id);
  if (item === undefined) throw new Response('Not found', { status: 404 });

  let myBids: Bid[] = [];
  let myBidsError = false;
  if (item.mode === 'AUCTION') {
    try {
      myBids = await loadMyBids(context, id);
    } catch {
      myBidsError = true;
    }
  }

  return {
    locale,
    item,
    defaultValidUntil: dateInputValue(new Date(), DEFAULT_VALIDITY_DAYS),
    thread: await loadMyThread(context, id),
    myBids,
    myBidsError,
    now: new Date().toISOString(),
    payouts: await loadMyPayouts(context),
    subscription: await loadMySubscription(context, { fresh: false }),
  };
}

/**
 * Send the quote, then let the loader say what happened.
 *
 * The feed is invalidated rather than patched: the row's `myQuote` is the server's to state, and the
 * list the provider goes back to must say *already quoted* too.
 */
export async function action({
  params,
  request,
  context,
}: ActionFunctionArgs): Promise<QuoteActionData | BidActionData | MessageActionData | null> {
  const form = await request.formData();
  const { queryClient, api } = context.get(routeContext);
  const jobId = params['id'] ?? '';

  /**
   * `W5-T01` §7: the gate refused a page that was stale about it. The form keeps what was typed and
   * shows the CTA line's sentence; the status is read again, so the banner appears with the way out.
   */
  async function payoutsRefusal(error: unknown): Promise<string | null> {
    const key = payoutsRefusalKey(error);
    if (key !== null) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.myPayouts(), refetchType: 'all' });
    }
    return key;
  }

  /** `W5-T13` §7: the same, one step earlier — the subscription, read again so the banner follows. */
  async function refusal(error: unknown): Promise<string | null> {
    const key = subscriptionRefusalKey(error);
    if (key === null) return payoutsRefusal(error);
    await queryClient.invalidateQueries({
      queryKey: queryKeys.mySubscription(),
      refetchType: 'all',
    });
    return key;
  }

  // `W6-T06`: bidding. The limits come from what the server said last — the feed row's ceiling and
  // this provider's own bids — so the screen can name the rule; the server checks both again.
  if (form.get('intent') === 'bid') {
    const item = (await loadJobFeed(context)).find((job) => job.id === jobId);
    const parsed = parseBidForm(form, {
      lowestCents: lowestOf(await loadMyBids(context, jobId)),
      maxPriceCents: item?.auction?.maxPriceCents ?? null,
    });
    if (!parsed.ok) return { bidErrors: parsed.errors };
    try {
      await api.placeBid(jobId, parsed.input);
    } catch (error) {
      return { bidFailure: (await refusal(error)) ?? bidFailureKey(error) };
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.myBids(jobId), refetchType: 'all' });
    return null;
  }

  if (form.get('intent') === 'withdraw') {
    try {
      await api.withdrawBid(String(form.get('bidId') ?? ''));
    } catch (error) {
      return { bidFailure: bidFailureKey(error) };
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.myBids(jobId), refetchType: 'all' });
    return null;
  }

  // `W4-T06`: a message to the client. Posted by a fetcher, so its answer never replaces the quote
  // form's; the loader that re-runs afterwards reads the thread fresh (`thread-query.ts`).
  if (form.get('intent') === 'message') {
    return sendMessage(form, (body) => api.sendMyThreadMessage(params['id'] ?? '', body));
  }

  const parsed = parseQuoteForm(form);
  if (!parsed.ok) return { errors: parsed.errors };

  try {
    await api.createQuote(params['id'] ?? '', parsed.input);
  } catch (error) {
    if (isRequestFull(error)) {
      // The feed is read again, so the row the provider goes back to says *Full* as well.
      await queryClient.invalidateQueries({ queryKey: queryKeys.jobFeed(), refetchType: 'all' });
      return { full: true };
    }
    // Not read again: the feed hides a closed request from a provider without a slot (E8), so a
    // fresh feed would turn this page into a 404 and lose the thread. The next read drops it.
    if (isRequestClosed(error)) return { closed: true };
    return { failure: (await refusal(error)) ?? quoteFailureKey(error) };
  }

  await queryClient.invalidateQueries({ queryKey: queryKeys.jobFeed(), refetchType: 'all' });
  return null;
}

/** `W5-T08` §7: `409 QUOTE_REQUEST_FULL` — the job has all the quotes it can take. */
function isRequestFull(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 409 && error.message === 'QUOTE_REQUEST_FULL'
  );
}

/** `W5-T12` §7: `409 QUOTE_REQUEST_CLOSED` — the client is not taking new quotes on this job. */
function isRequestClosed(error: unknown): boolean {
  return (
    error instanceof ApiError && error.status === 409 && error.message === 'QUOTE_REQUEST_CLOSED'
  );
}

/** `PENDING` and `ACCEPTED` hold the one slot a provider has on a job; the others freed it. */
function holdsSlot(item: JobFeedItem): boolean {
  return item.myQuote?.status === 'PENDING' || item.myQuote?.status === 'ACCEPTED';
}

export function Component(): ReactElement {
  const { t } = useTranslation();
  const {
    locale,
    item,
    defaultValidUntil,
    thread,
    myBids,
    myBidsError,
    now,
    payouts,
    subscription,
  } = useLoaderData<FeedJobData>();
  // `W5-T01` §4.3: the forms stay, so the provider sees what they will be able to do; only the send
  // waits for Stripe. An unread status gates nothing here — the API answers for it. `W5-T13`: the
  // subscription is the earlier step, so while it is pending the payouts banner and line wait.
  const unsubscribed = isSubscriptionPending(subscription);
  const gated = unsubscribed || (payouts !== null && !payouts.canQuote);
  const from = item.auction === null ? 'quote' : 'bid';
  const result = useActionData<QuoteActionData | BidActionData | null>();
  const navigation = useNavigation();
  usePoll();
  // `W4-T16`: what the provider has typed so far, so the IVA and total show before they send.
  const [typed, setTyped] = useState('');
  const typedCents = eurosToCents(typed);

  const errors = result !== undefined && result !== null && 'errors' in result ? result.errors : {};
  const failure =
    result !== undefined && result !== null && 'failure' in result ? result.failure : null;
  // `W5-T08`: full as the feed said it, or as the send just found out. Either way the form goes: the
  // API would refuse it, and what was typed is not shown back as an error (§4.5).
  const full = item.full || (result !== undefined && result !== null && 'full' in result);
  // `W5-T12`: closed, as the send just found out — the same reasoning, the same tone.
  const closed = result !== undefined && result !== null && 'closed' in result;
  // The floor in the provider's own currency format, from the contract's constant — never a literal.
  const min = {
    min: (QUOTE_MIN_AMOUNT_CENTS / 100).toLocaleString(locale, {
      style: 'currency',
      currency: 'EUR',
    }),
  };

  return (
    <article data-testid="feed-job">
      <p>
        <Link to={`/${locale}/feed`}>{t('feedJob.back')}</Link>
      </p>
      <h1>{item.title ?? t('jobs.untitled')}</h1>
      {item.auction === null ? null : (
        <AuctionHeader
          locale={locale}
          closesAt={item.auction.closesAt}
          maxPriceCents={item.auction.maxPriceCents}
        />
      )}
      <FeedWhere item={item} locale={locale} />
      {item.description === null ? null : <p>{item.description}</p>}
      <FeedBudget item={item} locale={locale} />
      <FeedCoverage item={item} locale={locale} />
      <SubscriptionRequiredBanner subscription={subscription} locale={locale} from={from} />
      {unsubscribed ? null : <PayoutsPendingBanner payouts={payouts} locale={locale} from={from} />}

      {item.auction !== null ? (
        <BidPanel
          auction={item.auction}
          myBids={myBids}
          myBidsError={myBidsError}
          now={now}
          locale={locale}
          busy={navigation.state !== 'idle'}
          result={result ?? null}
          payouts={payouts}
          subscription={subscription}
          gated={gated}
        />
      ) : (
        <section className="mp-section" aria-labelledby="feed-job-quote">
          <h2 id="feed-job-quote">{t('feedJob.quote.title')}</h2>

          {holdsSlot(item) ? (
            // No form at all while a quote holds the slot: the database allows one active quote per
            // provider per job, and a form it would refuse is a promise this screen cannot keep.
            <p data-testid="feed-job-quoted">
              {t(`feedJob.quote.sent.${item.myQuote?.status ?? 'PENDING'}`)}
            </p>
          ) : closed ? (
            <div role="status" data-testid="feed-job-closed">
              <p>
                <strong>{t('feedJob.quote.closed.title')}</strong>
              </p>
              <p>{t('feedJob.quote.closed.body')}</p>
            </div>
          ) : full ? (
            // The same reasoning, for the job's limit rather than the provider's: a status, in the
            // tone of any other notice, because nothing went wrong (ADR-017 §2).
            <div role="status" data-testid="feed-job-full">
              <p>
                <strong>{t('feedJob.quote.full.title')}</strong>
              </p>
              <p>{t('feedJob.quote.full.body')}</p>
            </div>
          ) : (
            <Form
              method="post"
              className="mp-form mp-stack-form"
              data-testid="quote-form"
              noValidate
            >
              {item.myQuote === null ? null : <p className="mp-meta">{t('feed.quoted.again')}</p>}

              <label htmlFor="quote-amount">{t('quoteForm.amount')}</label>
              <input
                id="quote-amount"
                name="amount"
                inputMode="decimal"
                autoComplete="off"
                onChange={(event) => setTyped(event.target.value)}
                aria-invalid={errors.amount === undefined ? undefined : true}
                aria-describedby={
                  errors.amount === undefined
                    ? 'quote-amount-hint'
                    : 'quote-amount-hint quote-amount-error'
                }
              />
              <p id="quote-amount-hint" className="mp-meta">
                {t('quoteForm.amountHint', min)}
              </p>
              <FieldError
                id="quote-amount-error"
                message={errors.amount === undefined ? undefined : t(errors.amount, min)}
              />
              {typedCents === undefined ? null : (
                <QuotePrice amountCents={typedCents} locale={locale} />
              )}

              <label htmlFor="quote-valid-until">{t('quoteForm.validUntil')}</label>
              <input
                id="quote-valid-until"
                name="validUntil"
                type="date"
                defaultValue={defaultValidUntil}
                {...invalidProps(errors.validUntil, 'quote-valid-until-error')}
              />
              <FieldError
                id="quote-valid-until-error"
                message={errors.validUntil === undefined ? undefined : t(errors.validUntil)}
              />

              <label htmlFor="quote-breakdown">{t('quoteForm.breakdown')}</label>
              <textarea
                id="quote-breakdown"
                name="breakdown"
                rows={4}
                {...invalidProps(errors.breakdown, 'quote-breakdown-error')}
              />
              <FieldError
                id="quote-breakdown-error"
                message={errors.breakdown === undefined ? undefined : t(errors.breakdown)}
              />

              {failure === null || isSubscriptionRefusal(failure) ? null : (
                <Alert tone="error">{t(failure, min)}</Alert>
              )}

              <Button
                type="submit"
                variant="primary"
                isDisabled={gated}
                isPending={navigation.state === 'submitting'}
                pendingLabel={t('quoteForm.pending')}
              >
                {t('quoteForm.submit')}
              </Button>
            </Form>
          )}
          {holdsSlot(item) || full || closed ? null : (
            <>
              <SubscriptionRefused failure={failure} locale={locale} from="quote" />
              {unsubscribed ? (
                <SubscriptionCtaLine subscription={subscription} locale={locale} from="quote" />
              ) : (
                <PayoutsCtaLine payouts={payouts} locale={locale} from="quote" />
              )}
            </>
          )}
        </section>
      )}

      {thread === null ? null : (
        <section className="mp-section" aria-labelledby="feed-job-thread">
          <h2 id="feed-job-thread">{t('thread.provider.title')}</h2>
          <p className="mp-meta">{t('thread.provider.intro')}</p>
          <Thread
            view={thread}
            locale={locale}
            viewer="PROVIDER"
            otherName={t('thread.clientName')}
            emptyText={t('thread.provider.empty')}
          />
        </section>
      )}
    </article>
  );
}

/**
 * `W6-T06` — the provider's side of an auction.
 *
 * **Only their own bids, ever** — the API returns nothing else, and the screen claims nothing else:
 * no count, no "you are winning". Lower only (operator, 2026-10-01): the form says the number to
 * beat, which is the provider's own lowest, and the client's ceiling. Once bidding is over, what the
 * last bid came to.
 *
 * `W6-T08`: open means the auction **says** `OPEN` and the deadline has not passed — an early award
 * (ADR-018) ends bidding before the clock does, and the panel says how it ended.
 */
function BidPanel({
  auction,
  myBids,
  myBidsError,
  now,
  locale,
  busy,
  result,
  payouts,
  subscription,
  gated,
}: {
  auction: NonNullable<JobFeedItem['auction']>;
  myBids: Bid[];
  myBidsError: boolean;
  now: string;
  locale: string;
  busy: boolean;
  result: QuoteActionData | BidActionData | null;
  /** `W5-T01`: what the CTA line reads, and whether the send waits for Stripe. Withdrawing never does. */
  payouts: PayoutStatus | null;
  /** `W5-T13`: the earlier step; while it is pending, its line replaces the payouts line. */
  subscription: ProviderSubscription | null;
  gated: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const money = (cents: number): string =>
    (cents / 100).toLocaleString(locale, { style: 'currency', currency: 'EUR' });

  const open = auction.status === 'OPEN' && timeLeft(auction.closesAt, now) !== null;
  const ended = auction.status === 'OPEN' || auction.status === 'CLOSED' ? null : auction.status;
  const active = myBids.find((bid) => bid.status === 'ACTIVE') ?? null;
  const latest = myBids[0] ?? null;
  const lowest = lowestOf(myBids);
  const errors = result !== null && 'bidErrors' in result ? result.bidErrors : {};
  const failure = result !== null && 'bidFailure' in result ? result.bidFailure : null;

  return (
    <section className="mp-section" aria-labelledby="feed-job-auction" data-testid="bid-panel">
      <h2 id="feed-job-auction">{t('bidPanel.title')}</h2>
      {auction.status === 'OPEN' ? <Countdown closesAt={auction.closesAt} now={now} /> : null}
      <p className="mp-meta">{t('bidPanel.sealed')}</p>
      {ended === null ? null : (
        <p className="mp-lead" data-testid="bid-auction-status">
          {t(`bidPanel.status.${ended}`)}
        </p>
      )}

      {active === null ? null : (
        <div data-testid="my-active-bid">
          <p className="mp-lead">{t('bidPanel.current', { amount: money(active.amountCents) })}</p>
          {open ? (
            <Form method="post">
              <input type="hidden" name="intent" value="withdraw" />
              <input type="hidden" name="bidId" value={active.id} />
              <Button type="submit" variant="secondary" isDisabled={busy}>
                {t('bidPanel.withdraw')}
              </Button>
            </Form>
          ) : null}
        </div>
      )}

      {myBidsError ? (
        <Alert tone="error">{t('bidPanel.loadFailed')}</Alert>
      ) : open ? (
        <Form method="post" className="mp-form mp-stack-form" data-testid="bid-form" noValidate>
          <input type="hidden" name="intent" value="bid" />
          {lowest === null ? null : (
            <p className="mp-meta" data-testid="bid-lower-than">
              {t('bidPanel.lowerThan', { amount: money(lowest) })}
            </p>
          )}

          <label htmlFor="bid-amount">{t('bidForm.amount')}</label>
          <input
            id="bid-amount"
            name="amount"
            inputMode="decimal"
            autoComplete="off"
            {...invalidProps(errors.amount, 'bid-amount-error')}
          />
          <FieldError
            id="bid-amount-error"
            message={errors.amount === undefined ? undefined : t(errors.amount)}
          />

          <label htmlFor="bid-message">{t('bidForm.message')}</label>
          <textarea
            id="bid-message"
            name="message"
            rows={3}
            maxLength={2000}
            {...invalidProps(errors.message, 'bid-message-error')}
          />
          <FieldError
            id="bid-message-error"
            message={errors.message === undefined ? undefined : t(errors.message)}
          />

          {failure === null || isSubscriptionRefusal(failure) ? null : (
            <Alert tone="error">{t(failure)}</Alert>
          )}

          <Button
            type="submit"
            variant="primary"
            isDisabled={gated}
            isPending={busy}
            pendingLabel={t('bidForm.pending')}
          >
            {t(active === null ? 'bidForm.submit' : 'bidForm.lower')}
          </Button>
        </Form>
      ) : ended === 'EXPIRED' || ended === 'CANCELLED' ? null : (
        <p className="mp-lead" data-testid="bid-result">
          {latest === null ? t('bidPanel.noBid') : t(`bidPanel.result.${latest.result}`)}
        </p>
      )}
      {!myBidsError && open ? (
        <>
          <SubscriptionRefused failure={failure} locale={locale} from="bid" />
          {isSubscriptionPending(subscription) ? (
            <SubscriptionCtaLine subscription={subscription} locale={locale} from="bid" />
          ) : (
            <PayoutsCtaLine payouts={payouts} locale={locale} from="bid" />
          )}
        </>
      ) : null}
    </section>
  );
}
